'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateWarehouseList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/warehouse/search');
};

export const useWarehouseDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, customFilters = []) => {
  const isFetchAll = page === undefined;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).addJoin('company', ['name']).setOrder(['name'], true);

    if (searchKey && searchValue) {
      if (searchKey === 'all') {
        fb.like('name', searchValue, 'OR');
        fb.like('warehouse_type', searchValue, 'OR');
        fb.like('location', searchValue, 'OR');
        fb.like('pic_name', searchValue, 'OR');
      } else {
        fb.like(searchKey, searchValue, 'AND');
      }
    }

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    if (customFilters && customFilters.length > 0) {
      customFilters.forEach((f) => {
        fb.where(f.operator || '=', f.field, f.value, f.logic || 'AND');
      });
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll, customFilters]);

  const key = useMemo(() => ['/warehouse/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getWarehouseById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/warehouse/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch warehouse by ID:', error);
    throw error;
  }
};

export const createWarehouse = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/warehouse/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateWarehouseList();
    return response.data;
  } catch (error) {
    console.error('Error creating warehouse:', error);
    throw error;
  }
};

export const updateWarehouse = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/warehouse/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateWarehouseList();
    mutate(`/warehouse/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating warehouse:', error);
    throw error;
  }
};

export const deleteWarehouse = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/warehouse/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateWarehouseList();
    return response.data;
  } catch (error) {
    console.error('Error deleting warehouse:', error);
    throw error;
  }
};
